import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Trash2, Plus, Minus, ShoppingBag, Tag, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Cart = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchCart();
  }, [user]);

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/cart`, { withCredentials: true });
      setCart(data);
      // Re-validate any applied coupon against new subtotal
      if (appliedCoupon && data.discounted_subtotal) {
        applyCouponInternal(appliedCoupon.code, data.discounted_subtotal, true);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/cart/${productId}`, { withCredentials: true });
      toast.success('Item removed from cart');
      fetchCart();
    } catch (error) { toast.error('Failed to remove item'); }
  };

  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await axios.put(`${BACKEND_URL}/api/cart/${productId}`, { quantity: newQuantity }, {
        params: { quantity: newQuantity },
        withCredentials: true,
      });
      fetchCart();
    } catch (error) { toast.error('Failed to update quantity'); }
  };

  const applyCouponInternal = async (code, orderTotal, silent = false) => {
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/coupons/apply`, {
        code,
        order_total: orderTotal,
      });
      setAppliedCoupon(data);
      if (!silent) toast.success(data.message);
      return true;
    } catch (error) {
      setAppliedCoupon(null);
      if (!silent) toast.error(error.response?.data?.detail || 'Invalid coupon');
      return false;
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) { toast.error('Enter a coupon code'); return; }
    if (!cart?.discounted_subtotal) { toast.error('Cart is empty'); return; }
    setApplyingCoupon(true);
    const ok = await applyCouponInternal(couponCode.trim().toUpperCase(), cart.discounted_subtotal);
    if (ok) setCouponCode('');
    setApplyingCoupon(false);
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    toast.success('Coupon removed');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C27054] mx-auto mb-4"></div>
          <p className="text-[#7A8A73]">Loading cart...</p>
        </div>
      </div>
    );
  }

  const items = cart?.items || [];
  const isEmpty = items.length === 0;
  const subtotal = cart?.subtotal || 0;
  const productDiscount = cart?.total_discount || 0;
  const afterProductDiscount = cart?.discounted_subtotal || 0;
  const couponDiscount = appliedCoupon?.discount || 0;
  const totalAfterCoupon = Math.max(afterProductDiscount - couponDiscount, 0);
  const GST_RATE = 0.03;
  const gstAmount = Math.round(totalAfterCoupon * GST_RATE * 100) / 100;
  const shipping = totalAfterCoupon >= 1000 ? 0 : 50;
  const grandTotal = totalAfterCoupon + gstAmount + shipping;

  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-6" data-testid="cart-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 md:mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73] mb-2">Shopping</p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26] leading-[1.1]">
            Your Cart
          </h1>
        </div>

        {isEmpty ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <ShoppingBag size={64} className="mx-auto text-[#7A8A73] mb-4" />
            <p className="text-[#7A8A73] text-lg mb-6">Your cart is empty</p>
            <Link
              to="/products"
              data-testid="continue-shopping-button"
              className="inline-block bg-[#C27054] text-white rounded-full px-8 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_14px_0_rgba(194,112,84,0.39)] hover:-translate-y-0.5 text-sm uppercase tracking-widest font-semibold"
            >
              Continue Shopping
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {items.map((item, index) => {
                const lineUnit = item.discounted_unit_price ?? item.unit_price ?? item.product?.price ?? 0;
                const originalUnit = item.unit_price ?? item.product?.price ?? 0;
                const hasDiscount = item.discount_percent > 0;
                return (
                  <motion.div
                    key={`${item.product_id}-${item.karat || 'na'}-${item.size || 'na'}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-4 md:p-6 flex gap-4 md:gap-6"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    {item.product && (
                      <>
                        <Link to={`/product/${item.product_id}`} className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0">
                          <img
                            src={item.product.image_url}
                            alt={item.product.name}
                            className="w-full h-full object-cover rounded-xl"
                          />
                        </Link>

                        <div className="flex-1 min-w-0">
                          <Link to={`/product/${item.product_id}`}>
                            <h3 className="text-base md:text-lg font-serif text-[#2D3B26] mb-1 hover:text-[#C27054] transition-colors line-clamp-1">
                              {item.product.name}
                            </h3>
                          </Link>
                          <p className="text-[10px] md:text-xs uppercase tracking-widest text-[#7A8A73]">
                            {item.karat ? `${item.karat} Gold` : item.product.metal_type}
                            {item.product.weight_grams ? ` · ${item.product.weight_grams}g` : ''}
                            {item.size ? ` · Size ${item.size}` : ''}
                          </p>
                          {item.price_breakdown && (
                            <p className="text-[10px] text-[#7A8A73] mt-1 hidden sm:block" data-testid={`item-breakdown-${item.product_id}`}>
                              ₹{item.price_breakdown.rate_per_gram}/g × {item.price_breakdown.weight_grams}g + ₹{item.price_breakdown.making_per_gram}/g making
                            </p>
                          )}
                          {hasDiscount && (
                            <span className="inline-block mt-2 text-[10px] uppercase tracking-wider bg-[#C27054]/10 text-[#C27054] px-2 py-0.5 rounded-full font-semibold">
                              {item.discount_label || `${item.discount_percent}% off`}
                            </span>
                          )}

                          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                            <div className="flex items-center gap-2 md:gap-3">
                              <button
                                onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                                data-testid={`decrease-quantity-${item.product_id}`}
                                className="w-7 h-7 md:w-8 md:h-8 bg-[#E4E8DF] rounded-full flex items-center justify-center hover:bg-[#C27054] hover:text-white transition-colors duration-300"
                              >
                                <Minus size={14} />
                              </button>
                              <span className="text-sm font-medium text-[#2D3B26] w-6 md:w-8 text-center" data-testid={`quantity-${item.product_id}`}>
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                                data-testid={`increase-quantity-${item.product_id}`}
                                className="w-7 h-7 md:w-8 md:h-8 bg-[#E4E8DF] rounded-full flex items-center justify-center hover:bg-[#C27054] hover:text-white transition-colors duration-300"
                              >
                                <Plus size={14} />
                              </button>
                            </div>

                            <div className="flex items-center gap-3 md:gap-4">
                              <div className="text-right">
                                {hasDiscount && (
                                  <p className="text-[10px] md:text-xs text-[#7A8A73] line-through">
                                    ₹{(originalUnit * item.quantity).toLocaleString('en-IN')}
                                  </p>
                                )}
                                <p className="text-base md:text-lg font-medium text-[#2D3B26]" data-testid={`item-total-${item.product_id}`}>
                                  ₹{(lineUnit * item.quantity).toLocaleString('en-IN')}
                                </p>
                              </div>
                              <button
                                onClick={() => removeItem(item.product_id)}
                                data-testid={`remove-item-${item.product_id}`}
                                className="text-[#7A8A73] hover:text-[#C27054] transition-colors"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-1"
            >
              <div className="bg-[#E4E8DF] rounded-2xl p-6 sticky top-32" data-testid="order-summary">
                <h2 className="text-xl font-serif text-[#2D3B26] mb-6">Order Summary</h2>

                {/* Apply Coupon */}
                <div className="mb-6 pb-6 border-b border-[rgba(45,59,38,0.1)]">
                  <p className="text-xs uppercase tracking-widest font-semibold text-[#475740] mb-2 flex items-center gap-1">
                    <Tag size={12} /> Coupon Code
                  </p>
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2" data-testid="applied-coupon">
                      <div>
                        <span className="font-mono font-bold text-[#C27054] text-sm">{appliedCoupon.code}</span>
                        <span className="text-xs text-[#475740] ml-2">−₹{appliedCoupon.discount.toLocaleString('en-IN')}</span>
                      </div>
                      <button
                        onClick={removeCoupon}
                        data-testid="remove-coupon-button"
                        className="text-[#7A8A73] hover:text-red-500 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                        data-testid="input-coupon-code"
                        className="flex-1 px-3 py-2 bg-white rounded-lg text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm uppercase"
                        placeholder="Enter code"
                      />
                      <button
                        onClick={applyCoupon}
                        disabled={applyingCoupon}
                        data-testid="apply-coupon-button"
                        className="px-4 py-2 bg-[#2D3B26] text-white rounded-lg hover:bg-[#1a2a1a] transition-colors text-xs font-semibold uppercase tracking-wider disabled:opacity-50"
                      >
                        {applyingCoupon ? '...' : 'Apply'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#475740]">Subtotal</span>
                    <span className="font-medium text-[#2D3B26]" data-testid="cart-subtotal">
                      ₹{subtotal.toLocaleString('en-IN')}
                    </span>
                  </div>
                  {productDiscount > 0 && (
                    <div className="flex justify-between text-sm" data-testid="cart-product-discount">
                      <span className="text-[#475740]">Product Discount</span>
                      <span className="font-medium text-green-700">−₹{productDiscount.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  {couponDiscount > 0 && (
                    <div className="flex justify-between text-sm" data-testid="cart-coupon-discount">
                      <span className="text-[#475740]">Coupon ({appliedCoupon.code})</span>
                      <span className="font-medium text-green-700">−₹{couponDiscount.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-[#475740]">Shipping</span>
                    <span className="font-medium text-[#2D3B26]">
                      {shipping === 0 ? 'Free' : `₹${shipping}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-testid="cart-gst">
                    <span className="text-[#475740]">GST (3%)</span>
                    <span className="font-medium text-[#2D3B26]">₹{gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="border-t border-[rgba(45,59,38,0.1)] pt-3 flex justify-between">
                    <span className="font-medium text-[#2D3B26]">Total</span>
                    <span className="text-xl font-serif text-[#C27054]" data-testid="cart-total">
                      ₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                <Link
                  to="/checkout"
                  state={{ appliedCoupon, totals: { subtotal, productDiscount, couponDiscount, gstAmount, shipping, grandTotal } }}
                  data-testid="proceed-to-checkout-button"
                  className="block w-full bg-[#C27054] text-white rounded-full px-8 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_14px_0_rgba(194,112,84,0.39)] hover:-translate-y-0.5 text-sm uppercase tracking-widest font-semibold text-center"
                >
                  Proceed to Checkout
                </Link>

                <Link
                  to="/products"
                  className="block text-center text-sm text-[#7A8A73] hover:text-[#C27054] transition-colors mt-4"
                >
                  Continue Shopping
                </Link>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
